import React, { useState, useEffect, useRef } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Button,
  Row,
  Col,
  PreviewAltCard,
  PreviewCard,
  BlockBetween,
} from "../../../components/Component";
import { DropdownToggle, DropdownMenu, UncontrolledDropdown, DropdownItem } from "reactstrap";
import { Link } from "react-router-dom";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";

const DashboardManagementPage = () => {
  const [sm, updateSm] = useState(false);
  const [data, setData] = useState({
    monthly_trend: [],
    sites_comparison: [],
    kpis: {
      total_observations: 0,
      positive_observations: 0,
      high_severity: 0,
      avg_quality_score: 0,
    },
  });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("year");
  const [alertStats, setAlertStats] = useState({
    total: 0,
    unread: 0,
    critical: 0,
    critical_over_sla: 0,
    critical_sla_minutes: 60,
  });
  const [criticalAlerts, setCriticalAlerts] = useState([]);
  const [takingOwnershipIds, setTakingOwnershipIds] = useState([]);
  const loadingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    
    const loadStats = async () => {
      // Éviter les appels multiples simultanés
      if (loadingRef.current) {
        return;
      }
      
      loadingRef.current = true;
      setLoading(true);
      
      try {
        const params = {};
        if (period === "month") {
          params.start_date = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
          params.end_date = new Date().toISOString().split('T')[0];
        } else if (period === "year") {
          params.start_date = new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0];
          params.end_date = new Date().toISOString().split('T')[0];
        }
        
        const [dashboardResponse, alertsResponse, criticalAlertsResponse] = await Promise.all([
          api.getManagementDashboard(params),
          api.getAlertStats(),
          api.getAlerts({
            page: 1,
            per_page: 5,
            status: "unread",
            severity: "critical",
          }),
        ]);

        if (!cancelled && dashboardResponse) {
          setData(dashboardResponse);
        }
        if (!cancelled && alertsResponse) {
          setAlertStats(alertsResponse);
        }
        if (!cancelled) {
          const alertsList = criticalAlertsResponse?.data || [];
          setCriticalAlerts(Array.isArray(alertsList) ? alertsList : []);
        }
      } catch (error) {
        if (!cancelled) {
          if (error.response?.status === 429) {
            toast.error("Trop de requêtes. Veuillez patienter quelques instants.");
          } else {
            toast.error("Erreur lors du chargement des statistiques");
          }
          console.error(error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
        loadingRef.current = false;
      }
    };

    loadStats();

    // Cleanup function pour annuler la requête si le composant est démonté
    return () => {
      cancelled = true;
    };
  }, [period]);

  const getMonthName = (month) => {
    const months = ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"];
    return months[month - 1] || month;
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return "A l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const handleTakeOwnership = async (alert) => {
    if (!alert?.id) return;
    setTakingOwnershipIds((prev) => [...prev, alert.id]);
    try {
      await api.takeAlertOwnership(alert.id);
      toast.success("Alerte prise en charge");
      const [alertsResponse, criticalAlertsResponse] = await Promise.all([
        api.getAlertStats(),
        api.getAlerts({
          page: 1,
          per_page: 5,
          status: "unread",
          severity: "critical",
        }),
      ]);
      setAlertStats(alertsResponse || {});
      setCriticalAlerts(Array.isArray(criticalAlertsResponse?.data) ? criticalAlertsResponse.data : []);
    } catch (error) {
      toast.error(error?.message || "Impossible de prendre en charge cette alerte");
    } finally {
      setTakingOwnershipIds((prev) => prev.filter((id) => id !== alert.id));
    }
  };

  if (loading) {
    return (
      <Content>
        <div className="text-center py-5">Chargement...</div>
      </Content>
    );
  }

  return (
    <React.Fragment>
      <Head title="Tableau de bord" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page tag="h3">
                Tableau de bord
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>Vue d&apos;ensemble HSE — tendances, sites et indicateurs clés</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <Button
                  className={`btn-icon btn-trigger toggle-expand me-n1 ${sm ? "active" : ""}`}
                  onClick={() => updateSm(!sm)}
                >
                  <Icon name="more-v"></Icon>
                </Button>
                <div className="toggle-expand-content" style={{ display: sm ? "block" : "none" }}>
                  <ul className="nk-block-tools g-3">
                    <li>
                      <UncontrolledDropdown>
                        <DropdownToggle tag="a" className="dropdown-toggle btn btn-white btn-dim btn-outline-light">
                          <Icon className="d-none d-sm-inline" name="calender-date"></Icon>
                          <span>
                            {period === "month" ? "Ce mois" : period === "year" ? "Cette année" : "Période"}
                          </span>
                          <Icon className="dd-indc" name="chevron-right"></Icon>
                        </DropdownToggle>
                        <DropdownMenu>
                          <ul className="link-list-opt no-bdr">
                            <li>
                              <DropdownItem
                                href="#dropdownitem"
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  setPeriod("month");
                                }}
                              >
                                Ce mois
                              </DropdownItem>
                            </li>
                            <li>
                              <DropdownItem
                                href="#dropdownitem"
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  setPeriod("year");
                                }}
                              >
                                Cette année
                              </DropdownItem>
                            </li>
                          </ul>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <Block>
          <Row className="g-gs" style={{ display: 'flex', flexWrap: 'wrap' }}>
            {/* KPI Cards */}
            <Col sm="6" lg="3" style={{ display: 'flex' }}>
              <PreviewAltCard style={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div className="card-title-group align-start pb-3" style={{ height: '70px' }}>
                  <div className="card-title">
                    <h6 className="title">Total observations</h6>
                    <p>Période sélectionnée</p>
                  </div>
                </div>
                <div className="amount" style={{ marginTop: 'auto' }}>
                  {data.kpis?.total_observations || 0}
                </div>
              </PreviewAltCard>
            </Col>
            <Col sm="6" lg="3" style={{ display: 'flex' }}>
              <PreviewAltCard style={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div className="card-title-group align-start pb-3" style={{ height: '70px' }}>
                  <div className="card-title">
                    <h6 className="title">Observations positives</h6>
                    <p>Bonnes pratiques</p>
                  </div>
                </div>
                <div className="amount" style={{ marginTop: 'auto' }}>
                  {data.kpis?.positive_observations || 0}
                </div>
              </PreviewAltCard>
            </Col>
            <Col sm="6" lg="3" style={{ display: 'flex' }}>
              <PreviewAltCard style={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div className="card-title-group align-start pb-3" style={{ height: '70px' }}>
                  <div className="card-title">
                    <h6 className="title">Haute gravité</h6>
                    <p>À traiter en priorité</p>
                  </div>
                </div>
                <div className="amount text-danger" style={{ marginTop: 'auto' }}>
                  {data.kpis?.high_severity || 0}
                </div>
              </PreviewAltCard>
            </Col>
            <Col sm="6" lg="3" style={{ display: 'flex' }}>
              <PreviewAltCard style={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div className="card-title-group align-start pb-3" style={{ height: '70px' }}>
                  <div className="card-title">
                    <h6 className="title">Score qualité moyen</h6>
                    <p>Sur 10</p>
                  </div>
                </div>
                <div className="amount" style={{ marginTop: 'auto' }}>
                  {data.kpis?.avg_quality_score != null && !isNaN(data.kpis.avg_quality_score) 
                    ? parseFloat(data.kpis.avg_quality_score).toFixed(1) 
                    : "0.0"}
                </div>
              </PreviewAltCard>
            </Col>

            {/* Bloc alertes critiques — uniquement s'il en existe */}
            {(alertStats.critical ?? 0) > 0 || criticalAlerts.length > 0 ? (
              <Col size="12">
                <PreviewCard className="card-full border border-danger">
                  <div className="card-title-group align-start mb-2">
                    <div className="card-title">
                      <h6 className="title text-danger">Alertes critiques en cours</h6>
                      <p className="text-soft mb-0">
                        Les alertes critiques doivent être prises en charge sous {alertStats.critical_sla_minutes || 60}{" "}
                        minutes.
                      </p>
                    </div>
                  </div>
                  <Row className="g-3 align-items-center">
                    <Col sm="6" lg="3">
                      <div className="nk-ecwg-data">
                        <div className="amount">{alertStats.critical || 0}</div>
                        <div className="info text-soft">Critiques totales</div>
                      </div>
                    </Col>
                    <Col sm="6" lg="3">
                      <div className="nk-ecwg-data">
                        <div className="amount text-warning">{alertStats.unread || 0}</div>
                        <div className="info text-soft">Non lues</div>
                      </div>
                    </Col>
                    <Col sm="6" lg="3">
                      <div className="nk-ecwg-data">
                        <div className="amount text-danger">{alertStats.critical_over_sla || 0}</div>
                        <div className="info text-soft">SLA dépassé</div>
                      </div>
                    </Col>
                    <Col sm="6" lg="3" className="text-sm-end">
                      <Button tag={Link} to="/alerts" color="danger">
                        <Icon name="alert-circle" />
                        <span>Traiter maintenant</span>
                      </Button>
                    </Col>
                  </Row>
                  <div className="table-responsive mt-3">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Alerte critique</th>
                          <th>Date</th>
                          <th className="text-end">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {criticalAlerts.length > 0 ? (
                          criticalAlerts.map((alert) => (
                            <tr key={alert.id}>
                              <td>
                                <div className="fw-medium">{alert.title || "Alerte critique"}</div>
                                <div className="small text-soft">{alert.message || ""}</div>
                              </td>
                              <td className="text-soft">{formatTime(alert.created_at)}</td>
                              <td className="text-end">
                                <div className="d-flex justify-content-end gap-1">
                                  {(alert?.deep_link ||
                                    alert?.data?.deep_link ||
                                    alert?.data?.observation_id) && (
                                    <Button
                                      size="sm"
                                      color="light"
                                      tag={Link}
                                      to={`${process.env.PUBLIC_URL || ""}${
                                        alert.deep_link ||
                                        alert.data?.deep_link ||
                                        `/observations/${alert.data.observation_id}/view`
                                      }`}
                                    >
                                      Voir
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    color="danger"
                                    disabled={takingOwnershipIds.includes(alert.id)}
                                    onClick={() => handleTakeOwnership(alert)}
                                  >
                                    {takingOwnershipIds.includes(alert.id) ? "..." : "Prendre en charge"}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center text-soft py-3">
                              Aucune alerte critique non lue.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </PreviewCard>
              </Col>
            ) : null}

            {/* Tendances mensuelles */}
            <Col size="12" lg="8">
              <PreviewCard className="card-full">
                <div className="card-title-group align-start mb-3">
                  <div className="card-title">
                    <h6 className="title">Tendances mensuelles</h6>
                    <p className="text-soft">Évolution des observations</p>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Mois</th>
                        <th>Année</th>
                        <th className="text-end">Nombre d'observations</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.monthly_trend?.length > 0 ? (
                        data.monthly_trend.map((item, index) => (
                          <tr key={index}>
                            <td>{getMonthName(item.month)}</td>
                            <td>{item.year}</td>
                            <td className="text-end">
                              <strong>{item.count}</strong>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center py-4">
                            Aucune donnée disponible
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </PreviewCard>
            </Col>

            {/* Comparaison des sites */}
            <Col size="12" lg="4">
              <PreviewCard className="card-full">
                <div className="card-title-group align-start mb-3">
                  <div className="card-title">
                    <h6 className="title">Comparaison des sites</h6>
                    <p className="text-soft">Performance HSE</p>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Site</th>
                        <th className="text-end">Observations</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.sites_comparison?.length > 0 ? (
                        data.sites_comparison.map((site, index) => (
                          <tr key={index}>
                            <td>{site.name || "N/A"}</td>
                            <td className="text-end">
                              <strong>{site.observations_count || 0}</strong>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="2" className="text-center py-4">
                            Aucune donnée disponible
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </PreviewCard>
            </Col>
          </Row>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default DashboardManagementPage;
